import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RegisterApiService } from '../core/services/register-api.service';
import { ApprovalApiService } from '../core/services/approval-api.service';

export interface AshCategoryItem {
  categoryName?: string;
  name?: string;
  quantity?: number;
  minQuantity?: number;
  maxQuantity?: number;
  qty?: number;
  minQty?: number;
  maxQty?: number;
}

export interface AshProductItem {
  productName?: string;
  name?: string;
  description?: string;
  otherDescription?: string;
}

export interface AshUnloadingItem {
  facilityName?: string;
  name?: string;
  description?: string;
  otherDescription?: string;
}

export interface PreviewDocument {
  documentTypeName?: string;
  documentName?: string;
  documentUploadId?: string | number;
  id?: string | number;
}

@Component({
  selector: 'app-user-preview',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './user-preview.html',
  styleUrl: './user-preview.scss',
})
export class UserPreview implements OnInit, OnDestroy {
  applicationNo = '';
  referenceNo = '';
  preview: any = null;
  loading = true;
  errorMessage = '';
  downloadingDocId: string | number | null = null;
  qParam: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private registerApi: RegisterApiService,
    private approvalApi: ApprovalApiService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    document.body.classList.add('user-preview-page');

    this.route.paramMap.subscribe((params) => {
      this.applicationNo = params.get('applicationNo')?.trim() || '';
      this.referenceNo = params.get('referenceNo')?.trim() || '';

      const q = this.route.snapshot.queryParamMap.get('q')?.trim();

      if (q) {
        this.qParam = q;
        this.referenceNo = q;
        this.loadPreview();
      } else if (!this.applicationNo || !this.referenceNo) {
        this.loading = false;
        this.errorMessage =
          'Invalid preview link. Application number and reference number are required.';
        this.cdr.detectChanges();
      } else {
        this.loadPreview();
      }
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('user-preview-page');
  }

  loadPreview(): void {
    this.loading = true;
    this.errorMessage = '';
    this.preview = null;
    this.cdr.detectChanges();

    const apiCall = this.qParam
      ? this.registerApi.getRegView(this.qParam)
      : this.registerApi.getRegistrationPreview(this.applicationNo, this.referenceNo);

    apiCall
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          if (res?.status === true && res.data) {
            this.preview = res.data;
          } else if (res?.data) {
            this.preview = res.data;
          } else if (res && typeof res === 'object' && Object.keys(res).length > 0) {
            // Support direct object response from getRegView api
            this.preview = res;
          } else {
            this.errorMessage =
              res?.message || 'No registration details found for the provided link.';
          }
        },
        error: (err) => {
          console.error('Registration preview API error:', err);
          this.errorMessage =
            err?.error?.message ||
            err?.message ||
            'Unable to load registration preview. Please verify the link and try again.';
        },
      });
  }

  get statusClass(): string {
    return (this.preview?.status || '').toLowerCase();
  }

  get ashCategories(): AshCategoryItem[] {
    const categories = this.preview?.ashCategories;
    return Array.isArray(categories) ? categories : [];
  }

  get ashProducts(): AshProductItem[] {
    const products = this.preview?.ashProducts;
    if (Array.isArray(products) && products.length) {
      return products;
    }
    if (this.preview?.flyAshProductName) {
      return [
        {
          productName: this.preview.flyAshProductName,
          description: this.preview.flyAshProductDescription,
        },
      ];
    }
    return [];
  }

  get ashUnloadingFacilities(): AshUnloadingItem[] {
    const facilities =
      this.preview?.ashUnloadingFacilities ||
      this.preview?.ashUnloadingFacility ||
      this.preview?.unloadingFacilities;

    if (Array.isArray(facilities) && facilities.length) {
      return facilities;
    }
    return [];
  }

  get documents(): PreviewDocument[] {
    const docs = this.preview?.documents;
    return Array.isArray(docs) ? docs : [];
  }

  getCategoryName(item: AshCategoryItem): string {
    return item?.categoryName || 'N/A';
  }

  getProductName(item: AshProductItem): string {
    return item?.productName || item?.name || 'N/A';
  }

  getProductDescription(item: AshProductItem): string {
    const name = (item?.productName || item?.name || '').toLowerCase();
    if (name !== 'others' && name !== 'other') return '';
    return item?.description || item?.otherDescription || '';
  }

  getFacilityName(item: AshUnloadingItem): string {
    return item?.facilityName || item?.name || 'N/A';
  }

  getFacilityDescription(item: AshUnloadingItem): string {
    const name = (item?.facilityName || item?.name || '').toLowerCase();
    if (name !== 'others' && name !== 'other') return '';
    return item?.description || item?.otherDescription || '';
  }

  displayValue(value: any, fallback = 'N/A'): string {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
  }

  formatDate(value: any): string {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getDocId(doc: PreviewDocument): string | number | null {
    return doc?.documentUploadId ?? doc?.id ?? null;
  }

  previewDocumentUrl: SafeResourceUrl | null = null;
  previewDocumentIsImage = false;

  closeDocumentPreview() {
    this.previewDocumentUrl = null;
    this.previewDocumentIsImage = false;
  }

  viewDocument(doc: PreviewDocument): void {
    const docId = this.getDocId(doc);
    if (!docId) return;

    this.downloadingDocId = docId;
    this.approvalApi.downloadDocument(docId).subscribe({
      next: async (blob: Blob) => {
        let type = blob.type;

        try {
          // Read the first 4 bytes to detect file type (magic numbers)
          const buffer = await blob.slice(0, 4).arrayBuffer();
          const view = new Uint8Array(buffer);
          let hex = '';
          for (let i = 0; i < view.length; i++) {
            hex += view[i].toString(16).padStart(2, '0').toUpperCase();
          }

          if (hex.startsWith('25504446')) {
            type = 'application/pdf'; // %PDF
          } else if (hex.startsWith('89504E47')) {
            type = 'image/png'; // PNG
          } else if (hex.startsWith('FFD8FF')) {
            type = 'image/jpeg'; // JPEG
          }
        } catch (e) {
          console.warn('Failed to read magic numbers for document preview', e);
        }

        if (!type || type === 'application/octet-stream' || type === 'application/json') {
          type = 'application/pdf';
        }

        const fileBlob = new Blob([blob], { type });
        const url = window.URL.createObjectURL(fileBlob);
        this.previewDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.previewDocumentIsImage = type.startsWith('image/');
        this.downloadingDocId = null;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to view document:', err);
        alert('Failed to open the document. Please try again.');
        this.downloadingDocId = null;
        this.cdr.detectChanges();
      },
    });
  }

  downloadDocument(doc: PreviewDocument): void {
    const docId = this.getDocId(doc);
    if (!docId) return;

    const fileName = doc.documentName || doc.documentTypeName || 'document.pdf';
    this.downloadingDocId = docId;

    this.approvalApi.downloadDocument(docId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
        this.downloadingDocId = null;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to download document:', err);
        alert('Failed to download the document. Please try again.');
        this.downloadingDocId = null;
        this.cdr.detectChanges();
      },
    });
  }

  isDocLoading(doc: PreviewDocument): boolean {
    const docId = this.getDocId(doc);
    return docId != null && this.downloadingDocId === docId;
  }

  getMachineryList(value: string | null | undefined): string[] {
    if (!value) return [];
    return value
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
  }
}
